import { jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, useState } from "react";
import { styled, IconButton as IconButton$1, Tooltip, CircularProgress, Alert as Alert$1, Fade, AlertTitle } from "@mui/material";
import { b as ICON_SIZE_MEDIUM, T as Typography } from "./TablePaginationActions-KpTvhN4Y.js";
const Option = styled("div")(({ theme }) => ({
  position: "relative",
  display: "inline-flex",
  // TODO: Remove this once we have a better way to handle the spacing between icon buttons:
  // https://app.shortcut.com/cartoteam/story/471284/create-iconbuttongroup-component-to-properly-group-several-icons
  "& + &, & + .optionIconButton": {
    marginLeft: theme.spacing(0.5)
  }
}));
const StyledIconButton = styled(IconButton$1, {
  shouldForwardProp: (prop) => !["active"].includes(prop)
})(({ active, theme }) => ({
  ...active && {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.background,
    "& svg:not(.doNotFillIcon) path": {
      fill: theme.palette.primary.main
    }
  }
}));
function _IconButton({
  tooltip,
  tooltipPlacement = "top",
  icon,
  size = "medium",
  variant = "icon",
  color = "default",
  sx,
  className,
  loading,
  disabled,
  active,
  ...props
}, ref) {
  return /* @__PURE__ */ jsx(Tooltip, { placement: tooltipPlacement, title: tooltip ?? "", children: /* @__PURE__ */ jsx(
    Option,
    {
      className: `optionIconButton ${className ?? ""}`,
      sx,
      "data-testid": "icon-button",
      children: /* @__PURE__ */ jsx(
        StyledIconButton,
        {
          ...props,
          ref,
          size,
          variant,
          color,
          active,
          disabled: disabled || loading,
          role: "button",
          children: loading ? /* @__PURE__ */ jsx(CircularProgress, { size: 18, color: "inherit" }) : icon
        }
      )
    }
  ) });
}
const IconButton = forwardRef(_IconButton);
const StyledAlert = styled(Alert$1, {
  shouldForwardProp: (prop) => ![
    "isNeutral",
    "content",
    "hasCloseButton",
    "hasAction",
    "hasTitle",
    "isSticky"
  ].includes(prop)
})(
  ({
    isNeutral,
    content,
    hasCloseButton,
    hasAction,
    hasTitle,
    isSticky,
    theme
  }) => ({
    columnGap: theme.spacing(1),
    minHeight: theme.spacing(6),
    ...hasAction && {
      display: "grid",
      gridTemplateAreas: content === "inline" || hasCloseButton ? `"icon message actions"` : `
    "icon message"
    "icon actions"
    `,
      gridTemplateColumns: hasCloseButton ? `${ICON_SIZE_MEDIUM} 1fr ${theme.spacing(3)}` : `${ICON_SIZE_MEDIUM}`
    },
    ...isNeutral && {
      backgroundColor: theme.palette.default.background,
      color: theme.palette.text.primary
    },
    ...isSticky && {
      borderRadius: 0
    },
    ".MuiAlert-message": {
      flex: 1,
      paddingTop: hasTitle ? theme.spacing(0.25) : theme.spacing(0.5),
      "& a": {
        color: theme.palette.primary.main,
        textDecoration: "none",
        "&:hover": {
          textDecoration: "underline"
        }
      },
      ...hasAction && {
        gridArea: "message"
      },
      ...isNeutral && {
        "& :not(.MuiAlertTitle-root)": {
          color: theme.palette.text.secondary
        },
        a: {
          color: `${theme.palette.primary.main} !important`
        }
      }
    },
    ".MuiAlert-icon": {
      height: hasTitle ? theme.spacing(2.5) : theme.spacing(3),
      marginRight: 0,
      ...hasAction && {
        gridArea: "icon"
      },
      ...isNeutral && {
        color: theme.palette.text.primary
      }
    },
    ".MuiAlert-action": {
      alignItems: content === "inline" && !hasCloseButton ? "center" : "flex-start",
      margin: content === "block" && !hasCloseButton ? theme.spacing(1.5, 0, 0.5) : 0,
      marginLeft: content === "inline" || hasCloseButton ? "auto" : 0,
      padding: 0,
      ...hasAction && {
        gridArea: "actions"
      },
      ".MuiIconButton-root svg path": {
        fill: hasCloseButton ? theme.palette.text.secondary : void 0
      }
    }
  })
);
function _Alert({
  title,
  action,
  severity = "neutral",
  content = "inline",
  variant = "standard",
  children,
  onClose,
  open: controlledOpen,
  isSticky,
  ...otherProps
}, ref) {
  const [open, setOpen] = useState(controlledOpen ?? true);
  const handleClose = onClose ? (ev) => {
    onClose(ev);
    setOpen(false);
  } : void 0;
  const isNeutral = severity === "neutral";
  const isOpen = controlledOpen ?? open;
  return /* @__PURE__ */ jsx(Fade, { in: isOpen, appear: false, children: /* @__PURE__ */ jsxs(
    StyledAlert,
    {
      ref,
      severity: isNeutral ? "info" : severity,
      isNeutral,
      content,
      action,
      onClose: handleClose,
      hasCloseButton: Boolean(onClose),
      hasAction: Boolean(action),
      hasTitle: Boolean(title),
      isSticky,
      variant,
      "data-name": "alert",
      "data-sticky": isSticky,
      "data-content": content,
      ...otherProps,
      children: [
        title && /* @__PURE__ */ jsx(AlertTitle, { children: title }),
        /* @__PURE__ */ jsx(Typography, { variant: "caption", color: "inherit", component: "div", children })
      ]
    }
  ) });
}
const Alert = forwardRef(_Alert);
export {
  Alert as A,
  IconButton as I
};
